import React, { useMemo } from "react";
import { AlertTriangle, ArrowRightLeft } from "lucide-react";

const normalizeSku = (value) => String(value || "").trim().toLowerCase();
const uniqueStrings = (values) =>
  Array.from(
    new Set((Array.isArray(values) ? values : []).map((value) => String(value || "").trim()).filter(Boolean))
  );

const ProductChip = ({ item, onOpenItem }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-3">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-slate-900">{item.name || "Unnamed product"}</div>
        <div className="mt-1 text-xs text-slate-500">
          Current SKU: {item.sku || "—"}
          {item.previousSkus?.length ? ` • Previous: ${item.previousSkus.join(", ")}` : ""}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onOpenItem?.(item.id)}
        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900"
      >
        Open
      </button>
    </div>
  </div>
);

export default function InventoryDuplicateResolver({ inventory = [], onOpenItem }) {
  const { duplicateCurrentSkus, historyConflicts } = useMemo(() => {
    const currentSkuOwners = new Map();
    const duplicateCurrentSkusMap = new Map();
    const historyConflictsMap = new Map();

    (Array.isArray(inventory) ? inventory : []).forEach((item) => {
      const currentSkuNorm = normalizeSku(item?.sku);
      if (currentSkuNorm) {
        const existing = currentSkuOwners.get(currentSkuNorm) || [];
        existing.push(item);
        currentSkuOwners.set(currentSkuNorm, existing);
      }
    });

    currentSkuOwners.forEach((items, skuNorm) => {
      if (items.length > 1) {
        duplicateCurrentSkusMap.set(skuNorm, {
          sku: items[0]?.sku || skuNorm,
          items,
        });
      }
    });

    (Array.isArray(inventory) ? inventory : []).forEach((item) => {
      uniqueStrings(item?.previousSkus).forEach((previousSku) => {
        const previousSkuNorm = normalizeSku(previousSku);
        if (!previousSkuNorm) return;
        const currentOwners = currentSkuOwners.get(previousSkuNorm) || [];
        const conflictingOwners = currentOwners.filter((owner) => owner.id !== item.id);
        if (!conflictingOwners.length) return;

        const key = `${item.id}:${previousSkuNorm}`;
        historyConflictsMap.set(key, {
          sku: previousSku,
          sourceItem: item,
          conflictingOwners,
        });
      });
    });

    return {
      duplicateCurrentSkus: Array.from(duplicateCurrentSkusMap.values()),
      historyConflicts: Array.from(historyConflictsMap.values()),
    };
  }, [inventory]);

  const clean = duplicateCurrentSkus.length === 0 && historyConflicts.length === 0;

  return (
    <div className="space-y-4">
      <div className="rounded-[24px] border border-slate-200 bg-gradient-to-br from-white via-amber-50 to-slate-50 p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
            <ArrowRightLeft className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Resolve Duplicate Products</h2>
            <p className="mt-1 text-sm text-slate-600">
              Use this owner/admin tool to spot SKU collisions before imports or support work get messy.
            </p>
          </div>
        </div>
      </div>

      {clean ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-8 text-center">
          <div className="text-sm font-semibold text-emerald-900">No SKU conflicts found</div>
          <div className="mt-1 text-sm text-emerald-700">
            Current SKUs and historical SKUs are clean right now.
          </div>
        </div>
      ) : null}

      {duplicateCurrentSkus.length > 0 ? (
        <div className="rounded-2xl border border-rose-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-rose-900">
            <AlertTriangle className="h-4 w-4" />
            Duplicate active SKUs
          </div>
          <div className="space-y-3">
            {duplicateCurrentSkus.map((group) => (
              <div key={group.sku} className="rounded-2xl border border-rose-100 bg-rose-50/60 p-3">
                <div className="mb-3 text-sm font-semibold text-rose-900">SKU {group.sku}</div>
                <div className="grid gap-3 md:grid-cols-2">
                  {group.items.map((item) => (
                    <ProductChip key={item.id} item={item} onOpenItem={onOpenItem} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {historyConflicts.length > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            SKU history conflicts
          </div>
          <div className="space-y-3">
            {historyConflicts.map((conflict) => (
              <div
                key={`${conflict.sourceItem.id}-${conflict.sku}`}
                className="rounded-2xl border border-amber-100 bg-amber-50/60 p-3"
              >
                <div className="mb-3 text-sm text-amber-900">
                  <span className="font-semibold">{conflict.sourceItem.name || "Unnamed product"}</span>
                  {` still carries historical SKU ${conflict.sku}, which now belongs to another active item.`}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <ProductChip item={conflict.sourceItem} onOpenItem={onOpenItem} />
                  {conflict.conflictingOwners.map((item) => (
                    <ProductChip key={item.id} item={item} onOpenItem={onOpenItem} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
